import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { cn } from "@/components/podkit/lib/cn";
import { toast } from "@/components/podkit/toasts/use-toast";
import { DeleteProjectModal } from "@/components/projects/ProjectDeleteModal";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import type { PlainOrganizationMember } from "@/queries/organization-queries";
import type { PlainProject, ProjectState } from "@/queries/project-queries";
import { CreateEnvironmentButton } from "@/routes/environments/create/CreateEnvironmentButton";
import { coalesce } from "@/utils/arrays";
import { formatError } from "@/utils/errors";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

type Props = {
    canEdit: boolean;
    project: PlainProject;
    state: ProjectState;
    orgMembers?: PlainOrganizationMember[];
    currentUserId: string;
    forceHoverState?: boolean;
    onCreateSuccess?: () => void;
    disableActions?: boolean;
};
export const ProjectCard: FC<Props> = ({
    canEdit,
    project,
    state,
    orgMembers,
    currentUserId,
    forceHoverState,
    onCreateSuccess,
    disableActions,
}) => {
    const projectContextUrl = getRepoUrlFromInitializer(project.initializer);
    const totalUsedBy = project.usedBy?.totalSubjects || 1;

    const creator = orgMembers?.find((m) => m.userId === project.metadata?.creator?.id);
    let users = coalesce(project.usedBy?.subjects?.map((s) => orgMembers?.find((m) => m.userId === s.id)) || []).slice(
        0,
        5,
    );
    users = users.length ? users : creator ? [creator] : [];
    const currentMember = orgMembers?.find((m) => m.userId === currentUserId);
    const isAdmin = currentMember?.role === OrganizationRole.ADMIN;
    const isCreator = project.metadata?.creator?.id === (currentUserId || "no-member");
    const editable = canEdit && (isAdmin || isCreator);

    const [active, setActive] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const handleMouseLeave = useCallback(() => {
        if (!menuOpen) {
            setActive(false);
        }
    }, [menuOpen]);

    const handleMenuOpenChange = useCallback(
        (newValue: boolean) => {
            if (!newValue) {
                setActive(false);
            }
            setMenuOpen(newValue);
        },
        [setActive, setMenuOpen],
    );

    // if we aren't showing any actions (disableActions), we want to use the whole card as an interactive button for creating environments
    const Comp = disableActions ? CreateEnvironmentButton : "div";
    const additionalComponentProps = disableActions
        ? {
              project,
              onCreateSuccess,
              hideTooltip: true,
          }
        : {};

    return (
        <Comp
            data-testid={"project-" + project.id}
            className={cn(
                "group flex h-auto max-w-full flex-row gap-2 rounded-xl border border-solid border-border-light bg-surface-glass px-6 py-3 text-left transition-shadow duration-200 hover:shadow",
                { "hover:bg-surface-primary": disableActions },
            )}
            data-active={active}
            onMouseEnter={() => setActive(true)}
            onMouseLeave={handleMouseLeave}
            {...additionalComponentProps}
        >
            <div className="min-w-0 grow">
                <div className="truncate text-lg font-medium leading-6 text-content-primary">
                    {project.metadata?.name}
                </div>
                <div className="truncate text-base leading-5 text-content-secondary">
                    {projectContextUrl?.replace(/https?:\/\//, "")}
                </div>
            </div>
            <div
                className={cn(
                    "flex shrink-0 flex-col justify-center",
                    { "group-data-[active=true]:hidden": !disableActions },
                    { hidden: forceHoverState },
                )}
            >
                <div className="inline-flex justify-end">
                    {users?.map((u, idx) => {
                        return (
                            <img
                                key={u.userId}
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                src={u.avatarUrl}
                                className={cn(
                                    "size-6 rounded-full border-[0.5px] border-black/50",
                                    idx > 0 && "ml-[-0.5rem]",
                                )}
                            />
                        );
                    })}
                    {totalUsedBy > 5 && (
                        <div
                            className={
                                "ml-[-0.5rem] size-6 rounded-full border-[0.5px] border-black/50 bg-gray-600 text-center text-sm leading-6 text-white"
                            }
                        >
                            +{Math.min(totalUsedBy, 9)}
                        </div>
                    )}
                </div>
            </div>
            {!disableActions && (
                <ProjectActions
                    project={project}
                    onCreateSuccess={onCreateSuccess}
                    editable={editable}
                    handleMouseLeave={handleMouseLeave}
                    handleMenuOpenChange={handleMenuOpenChange}
                    currentMember={currentMember}
                    state={state}
                    active={active}
                    creator={creator}
                />
            )}
        </Comp>
    );
};

type ProjectActionsProps = {
    project: PlainProject;
    forceHoverState?: boolean;
    editable?: boolean;
    menuOpen?: boolean;
    currentMember?: PlainOrganizationMember;
    state: ProjectState;
    active: boolean;
    creator?: PlainOrganizationMember;

    handleMenuOpenChange: (menuOpen: boolean) => void;
    handleMouseLeave?: () => void;
    onCreateSuccess?: () => void;
};
const ProjectActions: FC<ProjectActionsProps> = ({
    project,
    onCreateSuccess,
    forceHoverState,
    editable,
    menuOpen,
    handleMenuOpenChange,
    currentMember,
    state,
    active,
    creator,
}) => {
    const navigate = useNavigate();

    const onCopyId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(project.id);
            toast({
                title: "Project ID copied to clipboard",
                description: project.id,
            });
        } catch (error) {
            toast({
                title: "Failed to copy ID to your clipboard",
                description: formatError(error),
            });
        }
    }, [project.id]);

    const onCopyUrl = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/projects?project=${project.id}`);
        } catch (error) {
            toast({ title: "Failed to copy URL to your clipboard", description: formatError(error) });
        }
    }, [project.id]);

    const onEdit = useCallback(() => {
        navigate(`/projects/${project.id}`);
    }, [navigate, project.id]);

    const [showDeleteProjectModal, setShowDeleteProjectModal] = useState<{
        project: PlainProject;
        state: ProjectState;
        creator?: PlainOrganizationMember;
    }>();

    const onDelete = useCallback(
        () => setShowDeleteProjectModal({ project, creator, state }),
        [creator, project, state],
    );

    return (
        <>
            <div
                className={cn("hidden items-center gap-1.5 group-data-[active=true]:flex", {
                    flex: forceHoverState,
                })}
            >
                <CreateEnvironmentButton
                    project={project}
                    variant="primary"
                    className="h-8 py-1"
                    onCreateSuccess={onCreateSuccess}
                    hideTooltip={true}
                >
                    Create Environment
                </CreateEnvironmentButton>
                {active && (
                    <DropdownActions
                        open={menuOpen}
                        onOpenChange={handleMenuOpenChange}
                        triggerTestId="project-actions-dropdown-trigger"
                    >
                        <DropdownMenuItem onClick={onCopyId}>Copy ID</DropdownMenuItem>
                        <DropdownMenuItem onClick={onCopyUrl}>Copy URL</DropdownMenuItem>
                        {editable && (
                            <>
                                <DropdownMenuSeparator className="bg-content-tertiary/20" />
                                <DropdownMenuItem onClick={onEdit} data-testid="project-actions-dropdown-edit">
                                    Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={onDelete} className="text-content-red">
                                    Delete
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownActions>
                )}
            </div>

            {showDeleteProjectModal && (
                <DeleteProjectModal
                    project={project}
                    state={showDeleteProjectModal.state}
                    creator={showDeleteProjectModal.creator}
                    currentMember={currentMember}
                    onClose={() => setShowDeleteProjectModal(undefined)}
                />
            )}
        </>
    );
};

const PlainCard: FC<{ title: string; description: string; onClick: () => void }> = ({ title, description, onClick }) => {
    return (
        <button
            data-testid={"project-from-url"}
            className={cn(
                "group flex flex-row flex-wrap items-center justify-between gap-2",
                "rounded-xl border border-solid border-border-light bg-surface-glass px-6 py-3 text-left text-xs",
                "hover:bg-surface-primary",
            )}
            onClick={onClick}
        >
            <div className="min-w-0 flex-grow overflow-hidden">
                <div className="truncate text-base font-bold leading-6">{title}</div>
                <div className="truncate text-sm leading-5 text-content-secondary">{description}</div>
            </div>
        </button>
    );
};

export const NoProjectCard: FC<{ onCreateEnvironment: () => void }> = ({ onCreateEnvironment }) => {
    return (
        <PlainCard
            title="Open Repository URL"
            description="Quickly create an environment from a repo URL"
            onClick={onCreateEnvironment}
        />
    );
};

export const BlankEnvironmentCard: FC<{ onCreateEnvironment: () => void }> = ({ onCreateEnvironment }) => {
    return (
        <PlainCard
            title="Create Environment From Scratch"
            description="Start with a clean, empty environment"
            onClick={onCreateEnvironment}
        />
    );
};
